/**
 * Tenant Service - Centralized tenant state management
 * 
 * This module provides functions to manage tenantId in localStorage.
 * The tenantId is extracted from the JWT token after login.
 * 
 * Backend logic:
 * - If JWT contains tenantId, it is used
 * - Otherwise, X-Tenant header is read
 * 
 * Frontend should prefer tenantId from JWT (this module)
 */

// localStorage keys
const TENANT_ID_KEY = 'tenantId';
const TOKEN_KEY = 'authToken';

/**
 * Extract tenantId from JWT token payload
 * @param {string} token - JWT token string
 * @returns {string|null} - tenantId from token or null
 */
export const extractTenantIdFromToken = (token) => {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    //const payload = JSON.parse(atob(parts[1]));
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    return payload.tenantId || null;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[TenantService] JWT parse error", error);
    }
    return null;
  }
};

/**
 * Get tenantId from localStorage
 * @returns {string|null} - tenantId or null
 */
export const getTenantId = () => {
  return localStorage.getItem(TENANT_ID_KEY);
};

/**
 * Set tenantId in localStorage
 * @param {string} tenantId - The tenant ID to store
 */
export const setTenantId = (tenantId) => {
  if (tenantId) {
    localStorage.setItem(TENANT_ID_KEY, tenantId);
  }
};

/**
 * Clear tenantId from localStorage
 */
export const clearTenantId = () => {
  localStorage.removeItem(TENANT_ID_KEY);
};

/**
 * Get token from localStorage
 * @returns {string|null} - auth token or null
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Initialize tenant from existing token in localStorage
 * This is useful on app startup to restore tenant context
 * 
 * If token does not exist or tenantId cannot be extracted, clears any stale tenantId
 */
export const initializeTenantFromToken = () => {
  const token = getToken();
  if (token) {
    const tenantId = extractTenantIdFromToken(token);
    if (tenantId) {
      setTenantId(tenantId);
      return tenantId;
    }
  }
  // Token doesn't exist or tenantId cannot be extracted - clear any stale tenantId
  clearTenantId();
  return null;
};

/**
 * Clear all tenant-related data from localStorage
 */
export const clearAllTenantData = () => {
  clearTenantId();
  localStorage.removeItem('selectedTenant');
  localStorage.removeItem('selectedTenantId');
  localStorage.removeItem('selectedTenantCode');
};

const tenantService = {
  extractTenantIdFromToken,
  getTenantId,
  setTenantId,
  clearTenantId,
  getToken,
  initializeTenantFromToken,
  clearAllTenantData
};

export default tenantService;